import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import { Perfil, PerfilPermissoes, PerfisService } from '../../services/perfisService';

interface PerfilPermissoesModalProps {
  isOpen: boolean;
  onClose: () => void;
  perfil: Perfil | null;
  onSave: () => void;
}

const PerfilPermissoesModal: React.FC<PerfilPermissoesModalProps> = ({
  isOpen,
  onClose,
  perfil,
  onSave
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    ativo: true
  });
  const [permissoes, setPermissoes] = useState<PerfilPermissoes>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (perfil) {
      setFormData({
        nome: perfil.nome,
        descricao: perfil.descricao || '',
        ativo: perfil.ativo
      });
      setPermissoes(perfil.permissoes as PerfilPermissoes);
    } else {
      // Reset para novo perfil
      setFormData({
        nome: '',
        descricao: '',
        ativo: true
      });
      setPermissoes(PerfisService.getPermissoesPadrao());
    }
  }, [perfil]);

  const handlePermissaoChange = (modulo: keyof PerfilPermissoes, permissao: string, valor: boolean) => {
    setPermissoes(prev => ({
      ...prev,
      [modulo]: {
        ...prev[modulo],
        [permissao]: valor
      }
    } as PerfilPermissoes));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const perfilData = {
        nome: formData.nome,
        descricao: formData.descricao,
        permissoes: permissoes,
        ativo: formData.ativo
      };

      let result;
      if (perfil) {
        // Editar perfil existente
        result = await PerfisService.atualizar(perfil.id, perfilData);
      } else {
        // Criar novo perfil
        result = await PerfisService.criar(perfilData);
      }

      if (result.error) {
        const errorMessage = typeof result.error === 'string' 
          ? result.error 
          : result.error?.message || JSON.stringify(result.error);
        alert('Erro ao salvar perfil: ' + errorMessage);
        console.error('Erro detalhado:', result.error);
        return;
      }

      alert(perfil ? 'Perfil atualizado com sucesso!' : 'Perfil criado com sucesso!');
      onSave();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      alert('Erro ao salvar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const modulos = [
    {
      key: 'solicitacao_cadastro' as keyof PerfilPermissoes,
      label: '1° Solicitação de Cadastro',
      permissoes: [
        { key: 'aprovar_solicitacao', label: 'Aprovar ou reprovar solicitação de acesso' }
      ]
    },
    {
      key: 'gestao_agendamento' as keyof PerfilPermissoes,
      label: '2° Gestão de Agendamento',
      permissoes: [
        { key: 'ver', label: 'Ver' },
        { key: 'solicitar_pedido', label: 'Solicitar pedido' },
        { key: 'aprovar_solicitacao_agendamento', label: 'Aprovar solicitação de agendamento' },
        { key: 'reprovar_solicitacao_agendamento', label: 'Reprovar solicitação de agendamento' },
        { key: 'reagendar_solicitacao', label: 'Reagendar solicitação de agendamento' }
      ]
    },
    {
      key: 'gestao_pedidos' as keyof PerfilPermissoes,
      label: '3° Gestão de Pedidos de Compra',
      permissoes: [
        { key: 'ver', label: 'Ver' },
        { key: 'digitar_pedido', label: 'Digitar pedido de compra' }
      ]
    },
    {
      key: 'inicio' as keyof PerfilPermissoes,
      label: '4° Início',
      permissoes: [
        { key: 'ver', label: 'Ver' },
        { key: 'solicitar_agendamento', label: 'Solicitar agendamento' },
        { key: 'editar_murais', label: 'Editar murais de aviso e lembretes' }
      ]
    },
    {
      key: 'cargas' as keyof PerfilPermissoes,
      label: '5° Cargas',
      permissoes: [
        { key: 'ver', label: 'Ver' },
        { key: 'editar_ordem', label: 'Editar ordem de chegada' },
        { key: 'editar_ocorrencia', label: 'Editar ocorrência' },
        { key: 'editar_prioridade', label: 'Editar prioridade' },
        { key: 'editar_status', label: 'Editar status de carga' },
        { key: 'configurar_horario', label: 'Fechar ou reabrir horários de agendamento' },
        { key: 'fechar_dia', label: 'Fechar e reabrir dias de agendamento' }
      ]
    },
    {
      key: 'visitas' as keyof PerfilPermissoes,
      label: '6° Visitas',
      permissoes: [
        { key: 'ver', label: 'Ver módulo Visitas (geral)' },
        { key: 'ver_agendar_visitas', label: 'Ver menu Agendar Visitas' },
        { key: 'ver_gestao_visitas', label: 'Ver menu Gestão de Visitas' },
        { key: 'solicitar', label: 'Solicitar visita' },
        { key: 'aprovar', label: 'Aprovar visita' },
        { key: 'reprovar', label: 'Reprovar visita' },
        { key: 'reagendar', label: 'Reagendar visita' },
        { key: 'cancelar', label: 'Cancelar visita' },
        { key: 'ver_agenda', label: 'Ver subaba Minha Agenda' },
        { key: 'ver_checkin', label: 'Ver subaba Checkin' },
        { key: 'ver_solicitacoes', label: 'Ver subaba Solicitações' },
        { key: 'ver_indicadores', label: 'Ver subaba Indicadores' },
        { key: 'gerenciar_agenda', label: 'Gerenciar agenda'},
        { key: 'realizar_checkin', label: 'Realizar checkin e iniciar atendimento' },
        { key: 'finalizar_atendimento', label: 'Finalizar atendimento' },
        { key: 'historico', label: 'Ver histórico' }
      ]
    },
    {
      key: 'usuarios' as keyof PerfilPermissoes,
      label: 'Usuários',
      permissoes: [
        { key: 'ver', label: 'Ver' },
        { key: 'criar', label: 'Criar' },
        { key: 'editar', label: 'Editar' },
        { key: 'aprovar', label: 'Aprovar' },
        { key: 'gerenciar_perfis', label: 'Gerenciar Perfis' }
      ]
    },
    {
      key: 'bi_analitico' as keyof PerfilPermissoes,
      label: 'BI Analítico',
      permissoes: [
        { key: 'ver', label: 'Ver' }
      ]
    },
    {
      key: 'area_rca' as keyof PerfilPermissoes,
      label: 'Área RCA',
      permissoes: [
        { key: 'ver', label: 'Ver' }
      ]
    },
    {
      key: 'filiais' as keyof PerfilPermissoes,
      label: 'Filiais',
      permissoes: [
        { key: 'ver', label: 'Ver' }
      ]
    },
    {
      key: 'configuracoes' as keyof PerfilPermissoes,
      label: 'Configurações',
      permissoes: [
        { key: 'ver', label: 'Ver' }
      ]
    },
    {
      key: 'logs' as keyof PerfilPermissoes,
      label: 'Logs',
      permissoes: [
        { key: 'ver', label: 'Ver' }
      ]
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={perfil ? 'Editar Perfil' : 'Novo Perfil'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dados Básicos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Perfil *
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.ativo ? 'true' : 'false'}
              onChange={(e) => setFormData({ ...formData, ativo: e.target.value === 'true' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição
          </label>
          <textarea
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Descreva as responsabilidades deste perfil..."
          />
        </div>

        {/* Permissões */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Permissões</h3>
          
          <div className="max-h-96 overflow-y-auto space-y-4">
            {modulos.map((modulo) => (
              <div key={modulo.key} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{modulo.label}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {modulo.permissoes.map((permissao) => (
                    <label key={permissao.key} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(permissoes[modulo.key] as any)?.[permissao.key] || false}
                        onChange={(e) => handlePermissaoChange(modulo.key, permissao.key, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{permissao.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || !formData.nome}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Salvando...' : (perfil ? 'Atualizar' : 'Criar')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PerfilPermissoesModal;
